import { StatusData } from 'core/visitRequestReview/types';
import { SvgProps } from 'components/Elements/CustomSVGIcon/types';
import { ADMIN_ACCESS_LEVELS, ADMIN_USER, REQUEST_TYPE_ACCESS, SCORCHSOFT_USER, SUPER_ADMIN_USER } from 'core/utils/value';
import { AuthUserDataResponse } from 'core/user/types';
import { ApproveIcon, CancelIcon, ClockIcon, EditIcon, PushIcon, QueryIcon, RejectIcon, RestartIcon } from '../../../../components/Elements/CustomSVGIcon/index';

type AvailableApprover = {
    id: number;
    name: string;
};

export interface RequestAction {
    id: number,
    icon: React.ComponentType<SvgProps>,
    label: string,
    action: string,
    disabled: (statusData: StatusData,
        isCostApprover: boolean,
        user: AuthUserDataResponse,
        hasInvalidNsipVisitors?: boolean,
        allNsipVisitorsApproved?: boolean) => boolean,
    available: (
        user: AuthUserDataResponse,
        requester: AuthUserDataResponse | undefined | null,
        approver: AuthUserDataResponse[] | undefined | null,
        availableApprovers?: AvailableApprover[] | [] | undefined | null,
        costApprover?: AuthUserDataResponse | undefined | null,
        canAmend?: boolean | undefined,
    ) => boolean,
    iconStroke: (condition: boolean) => string,
}

export interface RequestActionPayload {
    id: number,
    Icon: React.ComponentType<SvgProps>,
    label: string,
    action: string,
    disabled: boolean,
    iconStroke: string,
}

export enum Actions {
    COST_APPROVE = 'cost_approve',
    APPROVE = 'approve',
    QUERY = 'query',
    REJECT = 'reject',
    SCHEDULE = 'schedule',
    PUSH = 'push',
    AMEND = 'amend',
    RESUBMIT = 'resubmit',
    CANCEL = 'cancel',
}

export const PENDING_STEP = 1;
export const APPROVE_STEP = 3;

// Admin, Scorchsoft admin and Super admin can all act on pending/approved requests
const hasElevatedAccess = (user: AuthUserDataResponse): boolean => ADMIN_ACCESS_LEVELS.includes(user.role.accessLevel);

const actions: RequestAction[] = [
    {
        id: 1,
        icon: ApproveIcon,
        label: 'Approve',
        action: Actions.APPROVE,
        disabled: (statusData, isCostApprover, user, _hasInvalidNsipVisitors, _allNsipVisitorsApproved) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.approve || isCostApprover
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            _requester,
            approver,
            _availableApprovers,
            costApprover,
        ) => (user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER)
            || (approver?.find(({ id }) => id === user.id)) !== undefined
            || (_availableApprovers?.find(({ id }) => id === user.id)) !== undefined
            || (user.email === costApprover?.email),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 2,
        icon: QueryIcon,
        label: 'Query',
        action: Actions.QUERY,
        disabled: (statusData, _isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.query
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            _requester,
            approver,
            _availableApprovers,
            costApprover,
        ) => (user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER)
            || (approver?.find(({ id }) => id === user.id)) !== undefined
            || (_availableApprovers?.find(({ id }) => id === user.id)) !== undefined
            || (user.email === costApprover?.email),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 3,
        icon: RejectIcon,
        label: 'Reject',
        action: Actions.REJECT,
        disabled: (statusData, isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.reject || isCostApprover
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            _requester,
            approver,
            _availableApprovers,
            costApprover,
        ) => (user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER)
            || (approver?.find(({ id }) => id === user.id)) !== undefined
            || (_availableApprovers?.find(({ id }) => id === user.id)) !== undefined
            || (user.email === costApprover?.email),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 4,
        icon: ClockIcon,
        label: 'Schedule',
        action: Actions.SCHEDULE,
        disabled: (statusData, isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.schedule || isCostApprover
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            _requester,
            _approver,
            _availableApprovers,
        ) => user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER,
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 5,
        icon: PushIcon,
        label: 'Push to Approver',
        action: Actions.PUSH,
        disabled: (statusData, isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.push || isCostApprover
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            _requester,
            _approver,
            _availableApprovers,
        ) => user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER,
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 6,
        icon: EditIcon,
        label: 'Amend Request',
        action: Actions.AMEND,
        // Not gated on outstanding cost approval: that gate is for the
        // approver-side actions. Amend belongs to the requester, and the
        // backend keeps it active at Pending Cost Approval (getStatusInfo).
        disabled: (statusData, _isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.amend
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        // The backend decides who may amend (requester, department
        // colleagues, approvers, admins) and sends it as canAmend.
        available: (
            user,
            requester,
            _approver,
            _availableApprovers,
            _costApprover,
            canAmend,
        ) => !!canAmend || user.email === requester?.email || hasElevatedAccess(user),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 7,
        icon: RestartIcon,
        label: 'Resubmit Request',
        action: Actions.RESUBMIT,
        // Requester-owned, so not gated on outstanding cost approval — see Amend.
        disabled: (statusData, _isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.resubmit
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            requester,
            _approver,
            _availableApprovers,
        ) => user.email === requester?.email || hasElevatedAccess(user),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
    {
        id: 8,
        icon: CancelIcon,
        label: 'Cancel',
        action: Actions.CANCEL,
        // Requester-owned, so not gated on outstanding cost approval — see Amend.
        // Otherwise a request sitting with the cost approver could not be
        // withdrawn by the person who raised it.
        disabled: (statusData, _isCostApprover, user) => {
            // Admins can always perform actions on pending/approved requests
            if (hasElevatedAccess(user)
                && (statusData.currentStatusStep === PENDING_STEP || statusData.currentStatusStep === APPROVE_STEP)) {
                return false;
            }
            return statusData.inActiveActions.cancel
                || ((statusData.currentStatusStep !== PENDING_STEP && statusData.currentStatusStep !== APPROVE_STEP)
                    && !hasElevatedAccess(user));
        },
        available: (
            user,
            requester,
            _approver,
            _availableApprovers,
        ) => user.email === requester?.email || hasElevatedAccess(user),
        iconStroke: (condition) => (condition ? '#C0D1D8' : ''),
    },
];

interface GetRequestActionsProps {
    statusData: StatusData,
    user: AuthUserDataResponse,
    requester: AuthUserDataResponse | undefined | null,
    approver: AuthUserDataResponse[] | undefined | null,
    availableApprovers: AvailableApprover[] | [] | undefined | null,
    costApprover: AuthUserDataResponse | undefined | null,
    hasCostApprove: boolean,
    hasInvalidNsipVisitors: boolean | undefined,
    allNsipVisitorsApproved: boolean | undefined,
    requestType?: string | undefined,
    canAmend?: boolean | undefined,
}

// Return array of Actions or empty array
export const getRequestActions = ({ statusData, user, requester, approver, availableApprovers, costApprover, hasCostApprove, hasInvalidNsipVisitors, allNsipVisitorsApproved, requestType, canAmend }: GetRequestActionsProps): RequestActionPayload[] => {
    // filter actions based on available condition
    const isAccessRequest = requestType === REQUEST_TYPE_ACCESS;
    const filteredActions = actions.filter((action) => {
        // Hide schedule action for access requests only — access doesn't
        // assign a security officer, so approval is the final step and the
        // request auto-completes once its dates pass. Guarding and visitor
        // requests still go through scheduling.
        if (isAccessRequest && action.action === Actions.SCHEDULE) {
            return false;
        }
        return action.available(user, requester, approver, availableApprovers, costApprover, canAmend);
    });
    let isCostApprover: boolean;
    if (costApprover === null) {
        isCostApprover = false;
    } else if (user.role.accessLevel === ADMIN_USER || user.role.accessLevel === SCORCHSOFT_USER || user.role.accessLevel === SUPER_ADMIN_USER) {
        isCostApprover = false;
    } else if (!hasCostApprove) {
        isCostApprover = user?.email !== costApprover?.email;
    } else {
        isCostApprover = user?.email === costApprover?.email;
    }

    const composedActions = filteredActions.map((action) => (
        {
            id: action.id,
            Icon: action.icon,
            label: action.label,
            action: action.action,
            disabled: action.disabled(statusData, isCostApprover, user, hasInvalidNsipVisitors, allNsipVisitorsApproved),
            iconStroke: action.iconStroke(action.disabled(statusData, isCostApprover, user, hasInvalidNsipVisitors, allNsipVisitorsApproved)),
        }
    ));

    return composedActions.length > 0 ? composedActions.sort((a, b) => a.id - b.id) : [];
};
